import { useState, useCallback, useEffect, useRef } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { EncryptionStyle } from "src/common/keys/encryption";
import { FSBlockType, FSMessage, FSRole, FSStatus, FSRequest, FSWrappedKey } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppDispatch, Message, MessageHandlerDisplayType, useSendRequestMutation, CollectionFilesyncAPI, DriveErrorMessages, AppUserKey, useGetGrants, useAppSelector,
  KeyFactory, Helpers, DriveRequest, UUID, GrantSetType, GrantSet, getEnumKey, ErrorStackItem, useGetPermissions, DownloadRequest,
  PermissionSetTypeMap, MaintainerGrantsMap, FetchS3MessageBlocks, pvConsoleLogger, useDownloadFileMutation, FileSizeLimits, MessageAnchors, BrowserType
} from "src/common";
import { PermissionSet, PermissionSetType } from "src/common/drive/permissions.class";
import { DriveCallbackAsyncFunction, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import saveAs from "file-saver";
import streamSaver from "streamsaver";
type OnProgressFunction = (request: DownloadRequest, done?: number, total?: number) => void;
type OnCompleteFunction = (request?: DownloadRequest, error_message?: string) => void;
type FileBlock = {
  index: number;
  data: Uint8Array;
}

/* Description: This is the download file hook that returns the file as a Blob. It is used for file viewer as well as downloading a file as an isolate operation */
export function useDownloadFileWeb(current_account: Account, use_filesync: boolean = false, onComplete: OnCompleteFunction, onProgress: OnProgressFunction) {
  const dispatch = useAppDispatch();
  const browser = useAppSelector((state: RootState) => state.app.browser);
  const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
  const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
  const [blocks, setBlocks] = useState<FileBlock[]>([]);
  const [file_progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const [fetchDownloadFile] = useDownloadFileMutation();
  const [sendRequest] = useSendRequestMutation();
  const { grants, error: grant_error, getGrants, destroyGrants } = useGetGrants(current_account);
  const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);

  // const [file_request, setFileRequest] = useState<DownloadRequest>();
  const file_request_ref = useRef<DownloadRequest>();
  function setFileRequest(file_request: DownloadRequest) {
    file_request_ref.current = file_request;
  }

  const total_chunks_ref = useRef<number>(1);
  const retry_count_ref = useRef<number>(0);
  function setRetryCount(_retry: number) {
    retry_count_ref.current = _retry;
  }
  // NOTE: Grants stored for reuse
  const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
  function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
    maintainer_grants_ref.current.set(id, maintainer_grants);
  }

  // NOTE: Permissions stored for reuse
  const permission_sets_ref = useRef<PermissionSetTypeMap>(new Map<string, PermissionSetType>);
  function setPermissionSetsRef(collection_id: string, permission_set: PermissionSetType) {
    permission_sets_ref.current.set(collection_id, permission_set);
  }
  function getPermissionSetRef(collection_id: string): PermissionSetType | undefined {
    return permission_sets_ref.current.get(collection_id);
  }

  useEffect(() => {
    if (!!permissions && !!file_request_ref.current) {
      setPermissionSetsRef(permissions.collection_id.B64(), permissions);
      destroyPermissions();
      downloadFileWeb(file_request_ref.current, permissions);
    }
  }, [permissions]);

  // Description: This block will only be hit if the file has a maintainer id and we call the getGrants, 
  // it will then call downloadBlockData with the appropriate user key so that it can decrypt the block's wrapped key. 
  useEffect(() => {
    if (!!grants && !!file_request_ref.current) {
      const _grants = Object.assign({}, grants);
      setMaintainerGrantsRef(grants.id, _grants);
      downloadBlockDataWithGrants(file_request_ref.current, _grants);
      destroyGrants();
    }

    !!grant_error &&
      handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.error_fetching_grant });
  }, [grants, grant_error]);

  // Description: Gets the file and goes through the data list, downloading each block. 
  async function downloadFileWeb(_file_request: DownloadRequest, _permissions: PermissionSetType) {
    const collection_id = _file_request.collection_id;
    const drive_request = await CollectionFilesyncAPI.fetchFile(current_account, collection_id, _file_request.file_id, _permissions);
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const fs_file = message.getFile();
        const file_size = fs_file?.getFileSize() || 0;
        if (file_size > FileSizeLimits.DRIVE_WEB_FILE_MAX_SIZE) {
          const size_limit_message = DriveErrorMessages.error_size_exceeds_limit.replace(MessageAnchors.file_size_limit, FileSizeLimits.DRIVE_UPLOAD_FILE_MAX_SIZE_GB.toString());
          handlePageErrorMessage(size_limit_message, { stack_message: size_limit_message }, message);
        } else if (!!fs_file && !!_permissions && fs_file.getDataList().length > 0) {
          total_chunks_ref.current = fs_file.getDataList().length;
          onProgress(_file_request, 0, total_chunks_ref.current);
          // NOTE: add file object to the Request
          const new_file_request = Object.assign({}, _file_request, { fs_file });
          setFileRequest(new_file_request);
          const maintainer_id = fs_file.getMaintainerId_asB64();
          if (fs_file.hasMaintainerId()) {
            // NOTE: If grants are saved in object use it directly else fetch grants
            const _maintainer_grants = maintainer_grants_ref.current.get(maintainer_id);
            !!_maintainer_grants ?
              downloadBlockDataWithGrants(new_file_request, _maintainer_grants) :
              getGrants(maintainer_id, collection_id, default_permissions, default_grants);
          } else {
            downloadBlocksData(new_file_request, fs_file.getDataList(), _permissions, undefined);
          }
        } else {
          handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.error_fetching_file }, message);
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.error_fetching_file }, message);
      }
    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_file);
  }

  // Description: Callback after grants are fetched
  async function downloadBlockDataWithGrants(_file_request: DownloadRequest, _grants: GrantSetType, _permissions?: PermissionSetType) {
    const fs_message_file = _file_request.fs_file;
    if (!!fs_message_file && fs_message_file?.getDataList().length > 0) {
      const wrapped_key = fs_message_file?.getDataList()[0].getWrappedKey();
      const grant = !!wrapped_key ? GrantSet.getGrantByRole(_grants, wrapped_key.getKeyVersion()) : null;
      const key = !!grant ? await grant.key(current_account) : null;
      !!key ? downloadBlocksData(_file_request, fs_message_file.getDataList(), _permissions, key) :
        handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
          stack_message: DriveErrorMessages.error_fetching_file_grant, stack_error:
            { fs_message_file: fs_message_file.toObject(), file_request: _file_request }
        });
    } else {
      handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.error_fetching_file, stack_error: { file_request: _file_request } });
    }
  }

  // Description: Downloads the blocks of the file
  async function downloadBlocksData(_file_request: DownloadRequest, file_blocks: FSMessage.Block[], _permissions?: PermissionSetType, key?: AppUserKey) {
    const block_ids: string[] = [];
    let block_collection_id = _file_request.collection_id; // NOTE: For POINTER TYPE The collection id will be different than the file ID
    file_blocks.forEach(async (block: FSMessage.Block, index: number) => {
      const block_type = block.getBlockType();
      const block_id = block.getId();
      switch (block_type) {
        // NOTE: Basic block is a block of a file that was uploaded into a directory after it was already shared
        case FSBlockType.BASIC:
          !!block_id && block_ids.push(block_id);
          (index === file_blocks.length - 1) &&
            downloadBlocks(_file_request, block_ids, block_collection_id, file_blocks, _permissions, key);
          break;
        case FSBlockType.POINTER: // NOTE: Pointer block is a block of a file that was already existing in the directory after sharing
          const wrapped_key = block.getWrappedKey();
          const set = !!_permissions ? PermissionSet.init(_permissions) : null;
          const version = !!wrapped_key ? wrapped_key?.getKeyVersion() : 0;
          const user_key = !!key ? key : !!set ? await set.permission(FSRole.READER, version)?.key(current_account) : null;
          if (!!user_key && !!block_id) {
            const decoded: Uint8Array = Helpers.hexDecode(block_id);
            const secret = await user_key.encryption_key.unseal(decoded);
            const data = Helpers.utf8Decode(secret).split("/");
            if (data.length !== 2) {
              handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.block_incorrect_format, stack_error: { file_request: _file_request } });
            } else {
              // NOTE: data[0] is a hex string so I am converting it to a UUID so we can get the B64() to get permissions/send the request
              block_ids.push(data[1]);
              block_collection_id = new UUID({ bytes: Helpers.hexDecode(data[0]) }).B64();
              index === file_blocks.length - 1 && downloadBlocks(_file_request, block_ids, block_collection_id, file_blocks, _permissions, key);
            }
          } else {
            handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.undefined_key, stack_error: { file_request: _file_request } });
          }
          break;
        default:
          handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
            stack_message: DriveErrorMessages.invalid_block_type,
            stack_error: { file_request: _file_request }
          });
          break;
      }
    });
  }

  // Description: Moved to DOWNLOAD_BLOCKS_DIRECT with a fallback of DOWNLOAD_BLOCKS with retries
  async function downloadBlocks(_file_request: DownloadRequest, block_ids: string[], block_collection_id: string,
    file_blocks: FSMessage.Block[], _permissions?: PermissionSetType, key?: AppUserKey, direct_fetch = true) {
    // NOTE: For files that have only one block and will be fast, add a slight increase in the progress bar
    file_blocks.length === 1 && onProgress(_file_request, 0.5, total_chunks_ref.current);
    const access_type = FSRequest.DownloadBlocks.BlockAccessType.DOWNLOAD_TO_BROWSER;
    const drive_request = direct_fetch ? await CollectionFilesyncAPI.downloadBlocksDirect(current_account, block_collection_id, block_ids, access_type) :
      await CollectionFilesyncAPI.downloadBlocks(current_account, block_collection_id, block_ids, access_type);
    async function callback(message: FSMessage) {
      const status = message.getStatus();
      const data_blocks = message.getDataBlocks()?.getBlocksList();
      if (status === FSStatus.OK && !!data_blocks) {
        handleFileBlocks(_file_request, block_ids, block_collection_id, data_blocks, file_blocks, _permissions, key, direct_fetch);
      } else {
        // NOTE: Failed, retry if under limit and if direct_fetch is true
        if (direct_fetch || retry_count_ref.current < FileSizeLimits.BLOCK_FETCH_RETRIES) {
          pvConsoleLogger(`[useDownloadFile Hook] - downloadBlocks Failed Retry: ${retry_count_ref.current}`);
          setRetryCount(Number(retry_count_ref.current + 1));
          downloadBlocks(_file_request, block_ids, block_collection_id, file_blocks, _permissions, key, false);
        } else {
          handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_message: DriveErrorMessages.error_downloading_block }, message);
        }
      }
    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_downloading_block);
  }

  // Description: Handle File Blocks URL or inline data
  async function handleFileBlocks(
    _file_request: DownloadRequest,
    block_ids: string[],
    block_collection_id: string,
    data_blocks: FSMessage.BlockData[],
    file_blocks: FSMessage.Block[],
    _permissions?: PermissionSetType,
    key?: AppUserKey,
    direct_fetch: boolean = true
  ) {
    let valid = true;
    let writer;
    // NOTE: Only initialize the writer if the browser is NOT Firefox
    if (!!browser && browser !== BrowserType.firefox) {
      const file_stream = streamSaver.createWriteStream(_file_request.file_name);
      writer = file_stream.getWriter();
    }

    try {
      const permissions_set = !!_permissions ? PermissionSet.init(_permissions) : null;
      for (let index = 0; index < data_blocks.length; index += 1) {
        const block_data = data_blocks[index];
        const fs_block = file_blocks[index];
        const wrapped_key = fs_block.getWrappedKey();
        const version = wrapped_key ? wrapped_key.getKeyVersion() : 0;
        const user_key = !!key ? key : (!!permissions_set ? await permissions_set.permission(FSRole.READER, version)?.key(current_account) : null);
        const block_id = block_data.getId();
        if (!!user_key && !!wrapped_key && !!block_id) {
          const data = await getBlockData(block_data, block_id, block_collection_id, user_key, wrapped_key);
          if (!!data) {
            if (!!writer) {
              // NOTE: Stream to disk and update progress on complete
              await writer.ready;
              await writer.write(data);
            } else {
              blocks.push({ data, index });
              setBlocks(blocks);
            }
            onProgress(_file_request, index, total_chunks_ref.current);
          } else {
            handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
              stack_message: DriveErrorMessages.error_downloading_block,
              stack_error: {
                block_data: block_data.toObject()
              }
            });
            break;
          }
        } else {
          valid = false;
          handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
            stack_message: DriveErrorMessages.undefined_key,
            stack_error: { action: "handleFileBlocks", file_request: _file_request }
          });
          break;
        }
      };

      // NOTE: finalize file on disk (abort if error)
      if (valid) {
        !!writer && await writer.close();
        handleSuccess(_file_request, !!writer);
      } else {
        !!writer && await writer.abort();
        // NOTE: Failed, retry if under limit and if direct_fetch is true
        if (direct_fetch || retry_count_ref.current < FileSizeLimits.BLOCK_FETCH_RETRIES) {
          pvConsoleLogger(`[useDownloadFile Hook] - handleFileBlocks Failed Retry: ${retry_count_ref.current}`);
          setRetryCount(Number(retry_count_ref.current + 1));
          downloadBlocks(_file_request, block_ids, block_collection_id, file_blocks, _permissions, key, false);
        }
      }
    } catch (error) {
      if (direct_fetch || retry_count_ref.current < FileSizeLimits.BLOCK_FETCH_RETRIES) {
        // NOTE: Failed, retry if under limit and if direct_fetch is true
        pvConsoleLogger(`[useDownloadFile Hook] - handleFileBlocks Failed Retry: ${retry_count_ref.current}`);
        setRetryCount(Number(retry_count_ref.current + 1));
        downloadBlocks(_file_request, block_ids, block_collection_id, file_blocks, _permissions, key, false);
      } else {
        !!writer && await writer.abort();
        handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
          stack_message: DriveErrorMessages.error_downloading_block,
          stack_error: { error },
        });
      }
    }
  }

  // Description: get the block data to either use with stream saver writer or accumulate to save file directly (Firefox) 
  async function getBlockData(
    block_data: FSMessage.BlockData,
    block_id: string,
    block_collection_id: string,
    user_key: AppUserKey,
    wrapped_key: FSWrappedKey) {
    let data;
    const unwrapped_block_key = await user_key.encryption_key.unseal(wrapped_key.getWrappedKey_asU8());
    const block_key = KeyFactory.deserializeSymmKey(unwrapped_block_key, EncryptionStyle.DRIVE);
    const block_url = block_data.getUrl();
    if (block_data.hasUrl() && !!block_url) {
      const block_blob = await FetchS3MessageBlocks(block_url, block_id, block_collection_id);
      const encrypted_data = Helpers.b64Decode(block_blob.data);
      data = await block_key.decrypt(encrypted_data);
    } else if (block_data.hasInlineData()) {
      data = await block_key.decrypt(block_data.getInlineData_asU8());
    }
    return data;
  }

  // Description: Handles successful download in web build mode
  function handleSuccess(_file_request: DownloadRequest, stream_saver: boolean) {
    if (!!blocks && blocks.length > 0 && !stream_saver) {
      const sorted_blocks = blocks.sort((a, b) => a.index - b.index);
      const data = sorted_blocks.map((b) => new Uint8Array(b.data));
      saveAs(new Blob(data), _file_request.file_name);
    }
    onProgress(_file_request, total_chunks_ref.current, total_chunks_ref.current);
    onComplete(_file_request);
    reset();
  }

  // Description: reset hook
  function reset(_error: boolean = false) {
    setError(_error);
    setProgress(0);
    setRetryCount(0);
    setBlocks([]);
    destroyGrants();
    destroyPermissions();
  }

  // ----------------------------------------------------------------------------------------------------
  // Description: Callbacks and general calls
  // ----------------------------------------------------------------------------------------------------
  // Description: Send request handles error when the request object is null
  function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveErrorMessages.error_sending_request) {
    if (!!request) {
      request.setCallback(callback);
      sendRequest(request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, { stack_error, stack_message: DriveErrorMessages.error_sending_request });
    }
  }

  // Description: Logs Errors and sets Error to true
  // Description: Handle error message and send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
    display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useDownloadFile Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    onComplete(file_request_ref.current, message);
    reset(true);
  }

  // ----------------------------------------------------------------------------------------------------
  // Description: Download file for app build mode
  // ----------------------------------------------------------------------------------------------------
  async function downloadFileForApp(_file_request: DownloadRequest) {
    onProgress(_file_request, 0.5, 1);
    await fetchDownloadFile({
      user_id: current_account.user_id,
      file_id: _file_request.file_id,
      collection_id: _file_request.collection_id,
      file_name: _file_request.file_name
    }).unwrap()
      .then(() => {
        handleSuccess(_file_request, false);
      })
      .catch((stack_error: unknown) => {
        handlePageErrorMessage(DriveErrorMessages.error_downloading_progress, {
          stack_message: DriveErrorMessages.error_downloading,
          stack_error
        });
      });
  }

  // Description: Callback for download file
  const downloadFile = useCallback((_file_request: DownloadRequest) => {
    if (use_filesync) {
      downloadFileForApp(_file_request);
    } else {
      setFileRequest(_file_request);
      const _permissions = getPermissionSetRef(_file_request.collection_id);
      !!_permissions ? downloadFileWeb(_file_request, _permissions) :
        getPermissions(_file_request.collection_id, default_permissions);
    }
  }, []);

  // Description: Callback for download file
  const resetFileDownload = useCallback(() => {
    reset();
  }, []);

  return {
    downloadFile,
    resetFileDownload,
    error,
    file_progress
  };
}
